import { DiscountProgress } from '@shared/composables/useShopifyDiscounts';
import { IAutomaticDiscount } from '@shared/types/IAutomaticDiscount';
import {
  cartGetters,
  DiscountAllocation,
  LineItem,
} from '@unified-commerce/gpc-vue-storefront-shopify';

import { HIDDEN_PRODUCT_TAG } from '~/constants';

const isQualifyingProduct = (discount: IAutomaticDiscount) => (lineItem: LineItem) =>
  discount.qualifyingProducts.map(({ id }) => id).includes(cartGetters.getProductId(lineItem));

const isDiscountGift = (discount: IAutomaticDiscount) => (lineItem: LineItem) =>
  cartGetters.getProductId(lineItem) === discount.id;

const sumLineItemQty = (acc: number, lineItem: LineItem) =>
  acc + cartGetters.getCartItemQuantity(lineItem);

const isHiddenProductLineItem = (lineItem: LineItem) =>
  cartGetters.getProductTags(lineItem).includes(HIDDEN_PRODUCT_TAG) ?? false;

const isNotDiscounted = (lineItem: LineItem) =>
  !cartGetters.getDiscountAllocations(lineItem).length;

const calculateProgressCounter = (
  { usesPerOrderLimit, numberItemsRequired }: IAutomaticDiscount,
  qualifyingProductsInCartQty: number,
) => {
  if (!usesPerOrderLimit) {
    return qualifyingProductsInCartQty;
  }
  const progressCounterCap = usesPerOrderLimit * numberItemsRequired;
  return Math.min(qualifyingProductsInCartQty, progressCounterCap);
};

const calculateNumberGiftsEligibleFor = (
  { numberItemsRequired, usesPerOrderLimit, quantity }: IAutomaticDiscount,
  qualifyingProductsInCartQty: number,
  giftsInCartQty: number,
) => {
  const totalPotentialRewards =
    Math.floor(qualifyingProductsInCartQty / numberItemsRequired) * quantity;
  if (!usesPerOrderLimit) {
    return Math.max(totalPotentialRewards - giftsInCartQty, 0);
  }
  const rewardCap = quantity * usesPerOrderLimit;
  return Math.max(Math.min(totalPotentialRewards, rewardCap) - giftsInCartQty, 0);
};

const calculateNextRewardLevel = ({
  counter,
  discount: { numberItemsRequired },
}: DiscountProgress) => Math.ceil(counter / numberItemsRequired) * numberItemsRequired;

export const calculateProgressPercent = ({
  counter,
  discount: { numberItemsRequired },
}: DiscountProgress): number => {
  const progressOffset = (count: number, numReqd: number) => {
    if (count === 0) return 0;
    const rem = counter % numberItemsRequired;
    // Remainder will be 0 when progress is 100%, e.g., 3/3 or 6/6
    return rem === 0 ? numReqd : rem;
  };

  return (progressOffset(counter, numberItemsRequired) / numberItemsRequired) * 100;
};

export const updateDiscountProgress = (
  discountProgress: DiscountProgress[],
  lineItems: LineItem[],
) => {
  discountProgress.forEach((discountProgress) => {
    const qualifyingProductsInCartQty = lineItems
      .filter(isQualifyingProduct(discountProgress.discount))
      .reduce(sumLineItemQty, 0);

    const giftsInCartQty = lineItems
      .filter(isDiscountGift(discountProgress.discount))
      .reduce(sumLineItemQty, 0);

    discountProgress.counter = calculateProgressCounter(
      discountProgress.discount,
      qualifyingProductsInCartQty,
    );
    discountProgress.numberGiftsEligibleFor = calculateNumberGiftsEligibleFor(
      discountProgress.discount,
      qualifyingProductsInCartQty,
      giftsInCartQty,
    );
    discountProgress.nextRewardLevel = calculateNextRewardLevel(discountProgress);
  });
};

export const hiddenProductsToRemove = (
  discountProgress: DiscountProgress[],
  lineItems: LineItem[],
) =>
  lineItems
    .filter(isHiddenProductLineItem)
    .filter(isNotDiscounted)
    .map((product) => ({ product }));

export const isFreeWithDiscount = (lineItem: LineItem) => {
  const price = cartGetters.getItemPrice(lineItem).price;
  if (!price) return false;
  const qty = cartGetters.getCartItemQuantity(lineItem);
  const discount = (cartGetters.getDiscountAllocations(lineItem) ?? [])
    .map(({ discountedAmount }: DiscountAllocation) => parseFloat(discountedAmount?.amount ?? '0'))
    .reduce((acc: number, val: number) => acc + val, 0);

  return discount === price * qty;
};
